/**
 * MeasureMagnifier — Iter 144 Phase 2 precision loupe.
 *
 * Pure visual aid that helps the field operator place measurement /
 * calibration points accurately on an iPad in sunlight where the finger
 * covers the exact target.
 *
 * Renders a 120px circular loupe positioned offset from the finger so
 * the loupe never sits under the touch point. The loupe shows the sheet
 * raster at 2x zoom centered on the current normalized hover coord, with
 * a center crosshair so the operator knows exactly where the next tap
 * will land.
 *
 * Hidden when:
 *   - mode is "off" (no measurement / calibration in progress)
 *   - no screen position has been observed yet
 *   - no raster URL is available
 *
 * Properties:
 *   rasterUrl    string  — sheet image URL (already loaded by SheetCanvas)
 *   hoverPoint   {nx,ny} — normalized (0..1) coord of current pointer
 *   screenPos    {x,y}   — clientX/clientY of finger
 *   active       bool    — measure or calibrate mode is on
 *   size         number  — loupe diameter in px (default 120)
 *   zoom         number  — magnification factor (default 2)
 */
import React from "react";

export default function MeasureMagnifier({
  rasterUrl,
  hoverPoint,
  screenPos,
  active,
  size = 120,
  zoom = 2,
}) {
  if (!active || !rasterUrl || !hoverPoint || !screenPos) return null;
  if (!Number.isFinite(hoverPoint.nx) || !Number.isFinite(hoverPoint.ny)) return null;
  if (!Number.isFinite(screenPos.x) || !Number.isFinite(screenPos.y)) return null;

  // Position the loupe offset from the finger so it never covers the target.
  // Default: up and to the left. Flip to the right if too close to the
  // left edge of the viewport; flip down if too close to the top.
  const OFFSET = 24;
  let x = screenPos.x - size - OFFSET;
  let y = screenPos.y - size - OFFSET;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1024;
  const vh = typeof window !== "undefined" ? window.innerHeight : 768;
  if (x < 8) x = screenPos.x + OFFSET;
  if (y < 8) y = screenPos.y + OFFSET;
  // Keep inside viewport
  if (x + size > vw - 8) x = vw - size - 8;
  if (y + size > vh - 8) y = vh - size - 8;

  // CSS background-position math:
  //   - The full raster is shown at (size * zoom * (1/displayedRasterFractionOnScreen)).
  //   - We use background-size with percentage of the loupe so that one
  //     "raster width" maps onto (rasterPxAtViewerScale × zoom). Practically
  //     we set background-size based on the raster's natural aspect ratio
  //     using fixed multipliers and rely on background-position to center.
  //
  // Simpler approach: render the raster at a fixed 2× of the viewer's
  // current displayed raster width. Because we don't have that in here,
  // we approximate with a "very large" background-size that gives the
  // operator a meaningful zoom in all realistic display configurations.
  // The loupe is a guide, not a measurement tool.
  const bgScale = 2400; // px — empirically a strong field zoom for 1500-3000px rasters at 50-100% viewer scale.
  // Background position: shift the image so (hoverPoint.nx, hoverPoint.ny)
  // ends up at the LOUPE center.
  const bgPosX = size / 2 - hoverPoint.nx * bgScale;
  const bgPosY = size / 2 - hoverPoint.ny * bgScale * (3 / 4); // approx aspect

  return (
    <div
      className="pointer-events-none fixed z-[70] rounded-full shadow-2xl border-2 border-[var(--fop-primary)] overflow-hidden"
      data-testid="measure-magnifier"
      style={{
        left: x,
        top: y,
        width: size,
        height: size,
        backgroundColor: "#0a0a0b",
        backgroundImage: `url(${rasterUrl})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: `${bgScale}px auto`,
        backgroundPosition: `${bgPosX}px ${bgPosY}px`,
      }}
    >
      {/* Center crosshair — the operator's exact target inside the loupe. */}
      <div
        className="absolute"
        style={{
          left: "50%", top: "50%",
          width: 1, height: 28,
          transform: "translate(-50%, -50%)",
          background: "var(--fop-primary)",
          boxShadow: "0 0 4px rgba(0,0,0,0.6)",
        }}
      />
      <div
        className="absolute"
        style={{
          left: "50%", top: "50%",
          width: 28, height: 1,
          transform: "translate(-50%, -50%)",
          background: "var(--fop-primary)",
          boxShadow: "0 0 4px rgba(0,0,0,0.6)",
        }}
      />
      {/* Small center dot for sub-pixel feedback */}
      <div
        className="absolute"
        style={{
          left: "50%", top: "50%",
          width: 4, height: 4,
          transform: "translate(-50%, -50%)",
          background: "var(--fop-primary)",
          borderRadius: "50%",
          border: "1px solid #fff",
        }}
      />
    </div>
  );
}
